import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import { slides, company, stats } from "../mock";
import CountUp from "./CountUp";

export default function HeroSlider() {
  const [index, setIndex] = useState(0);

  const go = useCallback((dir) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const t = setInterval(() => go(1), 7000);
    return () => clearInterval(t);
  }, [go]);

  const active = slides[index];
  const touchX = React.useRef(null);
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section className="relative bg-brand-ink overflow-x-clip" data-testid="hero-slider">
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="grain relative h-[82vh] h-[82svh] min-h-[580px] overflow-hidden"
      >
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${i === index ? "opacity-100" : "opacity-0"}`}
          >
            <div
              className={`absolute inset-0 bg-cover bg-center ${i === index ? "animate-kenburns" : ""}`}
              style={{ backgroundImage: `url(${s.image})` }}
            />
            {/* Directional cinematic scrim — photography stays visible */}
            <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/92 via-brand-ink/62 to-brand-ink/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-transparent to-brand-ink/25" />
          </div>
        ))}
        <div className="absolute inset-0 blueprint opacity-60 pointer-events-none" />
        <div className="glow pointer-events-none absolute -top-40 right-0 w-[760px] h-[760px] z-[5] opacity-60" />

        {/* Content */}
        <div className="relative z-10 h-full">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex flex-col justify-center">
            {/* Technical coordinate rail */}
            <div className="hidden lg:flex absolute left-10 top-1/2 -translate-y-1/2 -rotate-90 origin-center">
              <span className="font-mono text-[10px] uppercase tracking-[0.42em] text-white/35 whitespace-nowrap">
                40°44′N 30°01′E — Kartepe / Kocaeli
              </span>
            </div>

            <div className="lg:pl-16 max-w-2xl" key={index}>
              <div className="animate-fade-up">
                <div className="flex items-center gap-3.5 mb-8">
                  <span className="w-8 h-px bg-brand-glow/70" />
                  <span className="eyebrow text-brand-glow/90">{active.eyebrow}</span>
                </div>
                <h1 className="font-display font-light text-white text-[1.95rem] sm:text-4xl lg:text-[2.9rem] leading-[1.14] tracking-[-0.035em]">
                  {active.title}
                </h1>
                <div className="mt-7 hairline-dark w-32" />
                <p className="mt-7 text-sm md:text-base text-slate-300/85 max-w-md leading-relaxed font-light">
                  {active.text}
                </p>
                <div className="mt-11 flex flex-wrap items-center gap-4">
                  <Link
                    to="/uygulamalar"
                    data-testid="hero-applications-btn"
                    className="group font-mono text-[11px] font-medium uppercase tracking-[0.18em] bg-white hover:bg-brand-accent text-brand-ink hover:text-white pl-6 pr-3.5 py-3.5 rounded-full flex items-center gap-3 transition-colors duration-300"
                  >
                    Uygulamalarımız
                    <span className="w-7 h-7 rounded-full bg-brand-ink/10 group-hover:bg-white/20 flex items-center justify-center transition-[transform,background-color] duration-300 group-hover:rotate-45">
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                  <a
                    href={company.phoneHref}
                    data-testid="hero-phone-btn"
                    className="font-mono text-[11px] uppercase tracking-[0.18em] border border-white/25 hover:border-white/60 text-white px-6 py-3.5 rounded-full flex items-center gap-2.5 transition-colors duration-300"
                  >
                    <Phone className="w-3.5 h-3.5 text-brand-glow" /> {company.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <div className="absolute z-10 bottom-28 sm:bottom-36 left-6 right-6 sm:left-auto sm:right-10 flex items-center justify-between sm:justify-end gap-5">
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Slayt ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-px transition-all duration-500 ${i === index ? "w-10 bg-brand-glow" : "w-5 bg-white/30 hover:bg-white/70"}`}
              />
            ))}
          </div>
          <span className="font-mono text-white/45 text-[11px] tracking-[0.14em] tabular-nums">
            {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => go(-1)}
              aria-label="Önceki"
              data-testid="hero-prev-btn"
              className="w-10 h-10 rounded-full border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-brand-ink transition-colors duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Sonraki"
              data-testid="hero-next-btn"
              className="w-10 h-10 rounded-full border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-brand-ink transition-colors duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Glass stat console */}
      <div className="relative z-20 -mt-20 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 overflow-hidden border border-white/[0.09] bg-brand-ink/80 backdrop-blur-2xl shadow-[0_40px_80px_-40px_rgba(3,8,20,0.85)]">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className={`group relative px-7 py-8 lg:px-9 lg:py-10 transition-colors duration-500 hover:bg-white/[0.035] ${
                i !== 0 ? "lg:border-l border-white/[0.09]" : ""
              } ${i % 2 === 1 ? "border-l border-white/[0.09]" : ""} ${i > 1 ? "border-t lg:border-t-0 border-white/[0.09]" : ""}`}
            >
              <span className="font-mono text-[10px] tracking-[0.3em] text-white/25">0{i + 1}</span>
              <CountUp value={s.value} className="mt-3 block font-display font-light text-3xl lg:text-[2.6rem] leading-none text-white tabular-nums" />
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{s.label}</div>
              <span className="absolute left-0 bottom-0 h-px w-0 bg-brand-glow/70 transition-all duration-700 group-hover:w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
